import React, { useState, useEffect } from 'react';
import { CssBaseline, IconButton, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ToolArea from './ToolArea';
import Info from './Info';
import Viewport from './Viewport';
import Diagnosis from './Diagnosis';

import { Modal, PageLoader } from 'components';
import {
  PaperViewport,
  ReceivedAssignmentPaper,
  ViewportContainer,
  RootDiv,
} from './styles';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { modalWidths } from 'utils/styles';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import intl from 'react-intl-universal';
import logger from 'utils/logger';

// Create a logger instance for this component
const log = logger.createLogger('KenshinViewer');

const Viewer = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { updateLocalAssignments, viewerData, handleCloseViewer } =
    useOutletContext();

  const [currentCase, setCurrentCase] = useState(null);
  const [viewports, setViewports] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [openPrevCase, setOpenPrevCase] = useState(false);
  const [diagnosticNote, setDiagnosticNote] = useState('');

  const initViewport = async (currentCase, previousCase, nextCase) => {
    const currViewport = await createViewport(currentCase);
    const prevViewport = await createViewport(previousCase);
    const nextViewport = await createViewport(nextCase);

    setViewports({
      prevViewport: prevViewport,
      currViewport: currViewport,
      nextViewport: nextViewport,
      linkedPrevViewport: currentCase?.prevCase ? (
        <Viewport dicomFilePaths={currentCase.prevCase.webUrl} />
      ) : null,
    });
  };

  const initViewer = async () => {
    const currentIndex = viewerData.findIndex(
      (assignment) => assignment.id === parseInt(caseId)
    );
    const previousCase =
      currentIndex === 0 ? null : viewerData[currentIndex - 1];
    const nextCase =
      currentIndex === viewerData.length - 1
        ? null
        : viewerData[currentIndex + 1];
    await initViewport(currentCase, previousCase, nextCase);
  };

  // TODO Here the assignedCase is not exists
  const createViewport = async (assignedCase) => {
    if (assignedCase === null) {
      return null;
    }
    return <Viewport dicomFilePaths={assignedCase.webUrl} />;
  };

  useEffect(() => {
    const currentAssignment = viewerData.find(
      (element) => element.id === parseInt(caseId)
    );
    setCurrentCase(currentAssignment);
    initViewer().then(() => {
      setDiagnosticNote(currentAssignment.diagnosticNote);
      setLoaded(true);
    });
  }, [caseId, viewerData, loaded]);

  if (!loaded) {
    return <PageLoader />;
  }

  const handleGoToPrevCase = () => {
    const currentIndex = viewerData.findIndex(
      (assignment) => assignment.id === parseInt(caseId)
    );
    const previousCase =
      currentIndex === 0 ? null : viewerData[currentIndex - 1];

    if (previousCase === null) {
      // 0. issue the first one
      enqueueSnackbar(
        intl.get(
          'dashboard_received_assignment_kenshin_viewer_text_first_case'
        ),
        {
          variant: 'warning',
          autoHideDuration: 3000,
        }
      );
    }

    const newCaseId = previousCase.id;
    navigate(`/dashboard/assignment/received-kenshin/viewer/${newCaseId}`);
  };

  const handleGoToNextCase = () => {
    const currentIndex = viewerData.findIndex(
      (assignment) => assignment.id === parseInt(caseId)
    );
    const nextCase =
      currentIndex === viewerData.length - 1
        ? null
        : viewerData[currentIndex + 1];

    if (nextCase === null) {
      // 0. issue the first one
      enqueueSnackbar(
        intl.get('dashboard_received_assignment_kenshin_viewer_text_last_case'),
        {
          variant: 'warning',
          autoHideDuration: 3000,
        }
      );
    }

    const newCaseId = nextCase.id;
    navigate(`/dashboard/assignment/received-kenshin/viewer/${newCaseId}`);
  };

  return (
    <Modal
      isOpen
      testid="modal:assignment-viewer"
      width="100%"
      minWidth={modalWidths.md}
      withCloseIcon={false}
      onClose={() => handleCloseViewer()}
      renderContent={(modal) =>
        !openPrevCase ? (
          <RootDiv>
            <CssBaseline />
            <Grid container sx={{ height: '100%', padding: 2 }} spacing={2}>
              <Grid size={8}>
                <ViewportContainer container>
                  <PaperViewport>{viewports.currViewport}</PaperViewport>
                </ViewportContainer>
              </Grid>
              <Grid size={4}>
                <Grid size={2} sx={{ width: '100%' }}>
                  <IconButton
                    data-testid="assignment-viewer-button-close"
                    color="secondary"
                    onClick={modal.close}
                    size="large"
                    sx={{
                      float: 'right',
                      color: 'white',
                    }}
                    aria-label="close viewer">
                    <CloseIcon />
                  </IconButton>
                </Grid>
                <Grid sx={{ width: '100%' }} size={2}>
                  <ReceivedAssignmentPaper>
                    <ToolArea
                      hasPrevCase={viewports.prevViewport !== null}
                      hasNextCase={viewports.nextViewport !== null}
                      goToPrevCase={handleGoToPrevCase}
                      goToNextCase={handleGoToNextCase}
                    />
                  </ReceivedAssignmentPaper>
                </Grid>
                <Grid size={4} sx={{ width: '100%' }}>
                  <ReceivedAssignmentPaper>
                    <Info
                      currentCase={currentCase}
                      hasPrevCase={
                        currentCase.prevCase !== null &&
                        currentCase.prevCase !== undefined
                      }
                      openPrevCase={() => {
                        setOpenPrevCase(true);
                      }}
                    />
                  </ReceivedAssignmentPaper>
                </Grid>
                <Grid size={4} sx={{ width: '100%' }}>
                  <ReceivedAssignmentPaper>
                    <Diagnosis
                      diagnosis={null}
                      diagnosticNote={diagnosticNote}
                      setDiagnosticNote={setDiagnosticNote}
                      currentAssignment={currentCase}
                      updateLocalAssignments={updateLocalAssignments}
                      handleGoToNext={handleGoToNextCase}
                      showPrevCase={openPrevCase}
                      readOnly={false}
                    />
                  </ReceivedAssignmentPaper>
                </Grid>
              </Grid>
            </Grid>
          </RootDiv>
        ) : (
          <RootDiv>
            <Grid container spacing={2} sx={{ height: '100%', padding: 2 }}>
              <Grid size={6} sx={{ height: '65%' }}>
                <Paper sx={{ height: '100%', width: '100%' }}>
                  {viewports.linkedPrevViewport}
                </Paper>
              </Grid>
              <Grid size={6} sx={{ height: '65%' }}>
                <Paper sx={{ height: '100%', width: '100%' }}>
                  {viewports.currViewport}
                </Paper>
              </Grid>
              <Grid size={6} sx={{ height: '35%' }}>
                <ReceivedAssignmentPaper>
                  <Diagnosis
                    diagnosis={currentCase.prevCase.diagnosis}
                    diagnosticNote={currentCase.prevCase.diagnosticNote}
                    currentAssignment={currentCase}
                    setDiagnosticNote={setDiagnosticNote}
                    updateLocalAssignments={updateLocalAssignments}
                    showPrevCase={true}
                    closePrevCase={() => {
                      setOpenPrevCase(false);
                    }}
                    readOnly={true}
                  />
                </ReceivedAssignmentPaper>
              </Grid>
              <Grid size={6} sx={{ height: '35%' }}>
                <ReceivedAssignmentPaper>
                  <Diagnosis
                    diagnosis={null}
                    diagnosticNote={diagnosticNote}
                    setDiagnosticNote={setDiagnosticNote}
                    currentAssignment={currentCase}
                    updateLocalAssignments={updateLocalAssignments}
                    showPrevCase={true}
                    readOnly={false}
                  />
                </ReceivedAssignmentPaper>
              </Grid>
            </Grid>
          </RootDiv>
        )
      }
    />
  );
};

export default Viewer;
